/* ============================================================
   ProviderProductsPanel — seller listing management list:
   server-side paged/search/filter, publish state, archive.
   0e895f9a (a1a3b8f4 part 2): create/edit moved to the separate
   ProviderProductEditPage (/provider/products/new, /provider/products/:id);
   this panel keeps the list and delegates via onOpenEdit.
   ============================================================ */

import { useCallback, useEffect, useState } from 'react';
import { api } from '../api/client';
import type { ProductDetail } from '../types/product';
import { formatRub } from '../types/product';
import { ListPager } from './ListPager';
import { ProviderProductEditPage } from './ProviderProductEditPage';

/** 423ee061: seller listings page size (server-side paging). */
const MINE_PAGE_SIZE = 10;

/** 423ee061: status chip filters (server-side ProductStatus values). */
const STATUS_FILTERS: { value: string; label: string }[] = [
  { value: 'all', label: 'Все' },
  { value: 'Active', label: 'Активные' },
  { value: 'Archived', label: 'В архиве' },
];

interface Props {
  /** 'new' → create page, listing Guid → edit page, null → list. */
  editId: string | null;
  onOpenEdit: (id: string) => void;
  onCloseEdit: () => void;
}

export function ProviderProductsPanel({ editId, onOpenEdit, onCloseEdit }: Props) {
  const [products, setProducts] = useState<ProductDetail[]>([]);
  const [productsTotal, setProductsTotal] = useState(0);
  const [productOffset, setProductOffset] = useState(0);
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchDraft, setSearchDraft] = useState('');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  /* 423ee061: one server-side page of the seller's own listings —
   * status filter + title search applied in the database; changing a
   * filter resets to the first page. */
  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const page = await api.getMyProductsPage(
        statusFilter === 'all' ? '' : statusFilter,
        search.trim(),
        productOffset,
        MINE_PAGE_SIZE,
      );
      setProducts(page.items);
      setProductsTotal(page.total);
      // Archiving the last row of a trailing page — step back one page.
      if (page.items.length === 0 && productOffset > 0) {
        setProductOffset(Math.max(0, productOffset - MINE_PAGE_SIZE));
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Ошибка загрузки');
    } finally {
      setLoading(false);
    }
  }, [statusFilter, search, productOffset]);

  useEffect(() => {
    void load();
  }, [load]);

  // 0e895f9a: the whole tab becomes the edit page while an edit target is
  // active (URL-driven — /provider/products/new|:id). Kept after the hooks
  // so the hook order stays stable either way.
  if (editId !== null) {
    return <ProviderProductEditPage productId={editId === 'new' ? null : editId} onClose={onCloseEdit} />;
  }

  const archive = async (p: ProductDetail) => {
    setBusy(true);
    try {
      await api.archiveProduct(p.productId);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Не удалось удалить');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 'var(--space-md)' }}>
        <h2 style={{ fontSize: 'var(--fs-h2)', fontWeight: 'var(--fw-bold)' }}>Мои товары</h2>
        <button className="btn btn-primary" onClick={() => onOpenEdit('new')}>
          + Добавить товар
        </button>
      </div>

      {error && (
        <div className="card" style={{ padding: 'var(--space-md)', marginBottom: 'var(--space-md)' }}>
          <p style={{ color: 'var(--color-danger)' }}>{error}</p>
        </div>
      )}

      {/* 423ee061: server-side status chips + title search + pager */}
      <form
        style={{ display: 'flex', gap: 'var(--space-sm)', flexWrap: 'wrap', alignItems: 'center', marginBottom: 'var(--space-md)' }}
        onSubmit={(e) => {
          e.preventDefault();
          setProductOffset(0);
          setSearch(searchDraft);
        }}
      >
        {STATUS_FILTERS.map((f) => (
          <button
            key={f.value}
            type="button"
            className="btn"
            style={{
              height: 32,
              padding: '0 14px',
              fontSize: 'var(--fs-caption)',
              borderRadius: '999px',
              border: '1px solid var(--color-separator)',
              background: statusFilter === f.value ? 'var(--color-primary)' : 'var(--color-surface)',
              color: statusFilter === f.value ? '#fff' : 'var(--color-text-primary)',
            }}
            onClick={() => {
              setProductOffset(0);
              setStatusFilter(f.value);
            }}
          >
            {f.label}
          </button>
        ))}
        <input
          style={{
            height: 36,
            flex: '1 1 200px',
            padding: '0 var(--space-md)',
            borderRadius: 'var(--radius-md)',
            border: '1px solid var(--color-separator)',
            background: 'var(--color-surface)',
            fontSize: 'var(--fs-caption)',
          }}
          placeholder="Поиск по названию…"
          value={searchDraft}
          onChange={(e) => setSearchDraft(e.target.value)}
        />
        <button className="btn btn-secondary" type="submit" style={{ height: 36, padding: '0 16px', fontSize: 'var(--fs-caption)' }}>
          Найти
        </button>
      </form>

      {loading ? (
        <div style={{ textAlign: 'center' }}><div className="spinner" style={{ margin: '0 auto' }} /></div>
      ) : products.length === 0 ? (
        <div className="card" style={{ textAlign: 'center', padding: 'var(--space-xl)' }}>
          <p style={{ color: 'var(--color-text-secondary)' }}>
            {statusFilter !== 'all' || search
              ? 'По этим условиям ничего не найдено.'
              : 'У вас ещё нет товаров. Добавьте готовую модель с фиксированной ценой — она появится в магазине.'}
          </p>
        </div>
      ) : (
        <div style={{ display: 'grid', gap: 'var(--space-sm)' }}>
          {products.map((p) => (
            <div key={p.productId} className="card" style={{ padding: 'var(--space-md)', display: 'flex', alignItems: 'center', gap: 'var(--space-md)', flexWrap: 'wrap' }}>
              <div style={{ minWidth: 0, flex: '1 1 220px' }}>
                <div style={{ fontWeight: 'var(--fw-semibold)' }}>{p.title}</div>
                <div style={{ fontSize: 'var(--fs-caption)', color: 'var(--color-text-secondary)' }}>
                  {p.materialName} · {p.color} · срок {p.productionDays} дн.
                  {p.stockLeft === null ? ' · ∞' : ` · остаток ${p.stockLeft}`}
                  {` · мин ${p.minOrderQuantity}${p.maxOrderQuantity !== null ? ` / макс ${p.maxOrderQuantity}` : ''}`}
                </div>
              </div>
              <span style={{ fontWeight: 'var(--fw-bold)' }}>{formatRub(p.priceRub)}</span>
              {p.status === 'Active' || p.status === 1 ? (
                p.isPublished ? <span className="badge badge-success">В магазине</span> : <span className="badge">Скрыт</span>
              ) : (
                <span className="badge badge-warning">В архиве</span>
              )}
              <div style={{ display: 'flex', gap: 'var(--space-sm)', marginLeft: 'auto' }}>
                <button className="btn btn-secondary" style={{ height: 36, padding: '0 var(--space-md)' }}
                        onClick={() => onOpenEdit(p.productId)} disabled={busy}>
                  Изменить
                </button>
                <button style={{ color: 'var(--color-danger)', fontSize: 'var(--fs-caption)' }}
                        onClick={() => void archive(p)} disabled={busy}>
                  В архив
                </button>
              </div>
            </div>
          ))}

          <ListPager
            total={productsTotal}
            offset={productOffset}
            limit={MINE_PAGE_SIZE}
            onPrev={() => setProductOffset(Math.max(0, productOffset - MINE_PAGE_SIZE))}
            onNext={() => setProductOffset(productOffset + MINE_PAGE_SIZE)}
          />
        </div>
      )}
    </div>
  );
}
